/**
 * COMPLETION CHECKLIST CONFIGURATION
 * 
 * Defines the validation rules for Step 9 (Review & Complete)
 * Based on Document 5 specification
 */

// Labels for display
export const BUDGET_LABELS = {
  'under_5k': 'Under $5k',
  '5k_10k': '$5k-$10k',
  '10k_20k': '$10k-$20k',
  '20k_35k': '$20k-$35k',
  '35k_50k': '$35k-$50k',
  '50k_75k': '$50k-$75k',
  '75k_100k': '$75k-$100k',
  'over_100k': 'Over $100k',
};

export const TIMELINE_LABELS = {
  'asap': 'ASAP',
  '1_2_weeks': '1-2 weeks',
  '2_4_weeks': '2-4 weeks',
  '1_2_months': '1-2 months',
  '2_3_months': '2-3 months',
  '3_6_months': '3-6 months',
  '6_plus_months': '6+ months',
  'flexible': 'Flexible',
};

export const PRIORITY_LABELS = {
  'quality': 'Quality',
  'price': 'Price',
  'speed': 'Speed',
  'design': 'Design',
  'durability': 'Durability',
};

export const CONCERN_LABELS = {
  'budget_overrun': 'Budget Overrun',
  'timeline_delays': 'Timeline Delays',
  'quality_issues': 'Quality Issues',
  'communication': 'Communication',
  'disruption': 'Disruption',
  'hidden_costs': 'Hidden Costs',
  'contractor_reliability': 'Contractor Reliability',
};

/**
 * Completion checklist items
 * Each item has a validate function that takes the full field visit data
 */
export const COMPLETION_CHECKLIST = [
  {
    id: 'client_intel',
    label: 'Client Intel',
    step: 1,
    validate: (data) => {
      return !!(
        data.client_intel?.contact_name &&
        data.client_intel?.contact_phone
      );
    },
    getSummary: (data) => {
      const name = data.client_intel?.contact_name || 'Not provided';
      const phone = data.client_intel?.contact_phone || '';
      return `Contact: ${name}${phone ? `, ${phone}` : ''}`;
    },
    errorMessage: 'Missing contact name or phone',
  },
  {
    id: 'discovery',
    label: 'Client Discovery',
    step: 2,
    validate: (data) => {
      return !!(
        data.client_discovery?.problem_description &&
        data.client_discovery?.success_definition &&
        data.client_discovery?.budget_range
      );
    },
    getSummary: (data) => {
      const budget = BUDGET_LABELS[data.client_discovery?.budget_range] || 'Not set';
      const timeline = TIMELINE_LABELS[data.client_discovery?.timeline_desired_start] || 'Not set';
      return `Budget: ${budget} | Timeline: ${timeline}`;
    },
    errorMessage: 'Missing problem, success definition, or budget',
  },
  {
    id: 'decision_drivers',
    label: 'Decision Drivers',
    step: 3,
    validate: (data) => {
      return !!(
        data.decision_drivers?.priority &&
        data.decision_drivers?.biggest_concern
      );
    },
    getSummary: (data) => {
      const priority = PRIORITY_LABELS[data.decision_drivers?.priority] || 'Not set';
      const concern = CONCERN_LABELS[data.decision_drivers?.biggest_concern] || 'Not set';
      return `Priority: ${priority} | Concern: ${concern}`;
    },
    errorMessage: 'Missing priority or biggest concern',
  },
  {
    id: 'measurements',
    label: 'Measurements',
    step: 4,
    validate: (data) => {
      const areas = data.measurements?.areas || [];
      if (areas.length === 0) return false;
      
      // Check all areas have dimensions
      return areas.every(a =>
        a.dimensions?.length_ft || a.dimensions?.floor_sf
      );
    },
    getSummary: (data) => {
      const areas = data.measurements?.areas || [];
      if (areas.length === 0) return 'No areas added';
      
      const areaNames = areas.map(a => {
        const sf = a.dimensions?.floor_sf ? ` (${Math.round(a.dimensions.floor_sf)} SF)` : '';
        return `${a.label}${sf}`;
      });
      
      return `${areas.length} area${areas.length !== 1 ? 's' : ''}: ${areaNames.join(', ')}`;
    },
    errorMessage: 'Missing areas or dimensions',
  },
  {
    id: 'fixtures',
    label: 'Fixtures Identified',
    step: 4,
    validate: (data) => {
      const areas = data.measurements?.areas || [];
      
      // Check at least one fixture marked for work
      return areas.some(a =>
        Object.values(a.fixtures || {}).some(
          f => f.condition === 'replace' || f.condition === 'add_new'
        )
      );
    },
    getSummary: (data) => {
      let fixtureCount = 0;
      (data.measurements?.areas || []).forEach(a => {
        fixtureCount += Object.values(a.fixtures || {}).filter(
          f => f.condition === 'replace' || f.condition === 'add_new'
        ).length;
      });
      return `${fixtureCount} fixture${fixtureCount !== 1 ? 's' : ''} marked for replacement`;
    },
    errorMessage: 'No fixtures marked for replacement',
  },
  {
    id: 'photos',
    label: 'Photos Captured',
    step: 5,
    validate: (data, settings) => {
      const minRequired = settings?.step5_settings?.min_photos_required || 1;
      const photos = data.photos?.items || [];
      return photos.length >= minRequired;
    },
    getSummary: (data) => {
      const photoCount = data.photos?.items?.length || 0;
      return `${photoCount} photo${photoCount !== 1 ? 's' : ''} uploaded`;
    },
    errorMessage: 'Minimum photos not captured',
  },
  {
    id: 'products',
    label: 'Products Selected',
    step: 6,
    validate: (data) => {
      const selections = data.product_selections?.selections || [];
      return selections.length > 0;
    },
    getSummary: (data) => {
      const selections = data.product_selections?.selections || [];
      const total = selections.reduce((sum, s) => sum + (s.client_price || 0), 0);
      return `${selections.length} product${selections.length !== 1 ? 's' : ''} | Total: $${total.toLocaleString()}`;
    },
    errorMessage: 'No products selected',
  },
  {
    id: 'risks',
    label: 'Risks Documented',
    step: 7,
    validate: (data, settings) => {
      const minRequired = settings?.step7_settings?.min_risks_required || 0;
      const risks = data.risks?.items || [];
      return risks.length >= minRequired;
    },
    getSummary: (data) => {
      const risks = data.risks?.items || [];
      const contingency = risks
        .filter(r => r.contingency_included && r.contingency_amount)
        .reduce((sum, r) => sum + (parseFloat(r.contingency_amount) || 0), 0);
      
      let summary = `${risks.length} risk${risks.length !== 1 ? 's' : ''}`;
      if (contingency > 0) {
        summary += ` | $${contingency.toLocaleString()} contingency`;
      }
      return summary;
    },
    errorMessage: 'Minimum risks not documented',
    optional: true,  // Warning only, not blocking
  },
  {
    id: 'scope',
    label: 'Scope Description',
    step: 8,
    validate: (data, settings) => {
      const minChars = settings?.step8_settings?.min_characters || 100;
      const narrative = data.scope_description?.narrative || '';
      return narrative.length >= minChars;
    },
    getSummary: (data) => {
      const charCount = (data.scope_description?.narrative || '').length;
      return `${charCount} characters`;
    },
    errorMessage: 'Scope description too short',
  },
];

/**
 * Run checklist validation
 * @param {Object} data - Full field visit data
 * @param {Object} settings - Field visit settings
 * @returns {Array} Results with passed/failed status for each item
 */
export const runChecklistValidation = (data, settings = {}) => {
  return COMPLETION_CHECKLIST.map(item => ({
    ...item,
    passed: item.validate(data, settings),
    summary: item.getSummary(data),
  }));
};

/**
 * Check if all required items pass
 * @param {Object} data - Full field visit data
 * @param {Object} settings - Field visit settings
 * @returns {Boolean} True if all required (non-optional) items pass
 */
export const allRequiredItemsPass = (data, settings = {}) => {
  const results = runChecklistValidation(data, settings);
  return results
    .filter(item => !item.optional)
    .every(item => item.passed);
};

export default {
  COMPLETION_CHECKLIST,
  BUDGET_LABELS,
  TIMELINE_LABELS,
  PRIORITY_LABELS,
  CONCERN_LABELS,
  runChecklistValidation,
  allRequiredItemsPass,
};
